import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Monitor, FileText, Package, Truck, BarChart3,
  Scan, Wifi, Clock, Check, Users, Receipt,
  CreditCard, ShieldCheck, Printer
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const POS_MOCKUP = 'https://static.prod-images.emergentagent.com/jobs/81285e37-432c-430a-b239-6fe7ab6d07e8/images/c10e7f68644a1fff331939ae1510bc3849ec584c4399a8172cb048aefb6f4343.png';

const FeaturesSection = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      icon: Monitor,
      label: t('feature_tab_pos') || 'Point of Sale',
      title: t('feature_pos_title') || 'Complete POS System',
      desc: t('feature_pos_long_desc') || 'A touch-optimized POS system that works on any device. Process orders quickly, split bills, handle cash and card payments, and print thermal receipts. Works offline so you never miss a sale.',
      features: [
        t('feature_pos_f1') || 'Quick order entry with product grid',
        t('feature_pos_f2') || 'Cash & card payment processing',
        t('feature_pos_f3') || 'USB barcode scanner support',
        t('feature_pos_f4') || 'ESC/POS thermal receipt printing',
        t('feature_pos_f5') || 'Staff PIN lock for shared terminals',
        t('feature_pos_f6') || 'Full offline mode with auto-sync',
      ],
      image: POS_MOCKUP,
    },
    {
      icon: FileText,
      label: t('feature_tab_invoicing') || 'Invoicing',
      title: t('feature_invoice_title') || 'VeriFactu Invoices',
      desc: t('feature_invoice_long_desc') || 'Create legally compliant invoices that meet Spain\'s VeriFactu regulations. Every invoice includes a cryptographic hash chain, QR code, and is automatically submitted to AEAT.',
      features: [
        t('feature_inv_f1') || 'VeriFactu-compliant hash chain',
        t('feature_inv_f2') || 'QR code on every invoice',
        t('feature_inv_f3') || 'Automatic AEAT submission',
        t('feature_inv_f4') || 'PDF generation and email delivery',
        t('feature_inv_f5') || 'Recurring invoice automation',
        t('feature_inv_f6') || 'Client directory with history',
      ],
      image: null,
    },
    {
      icon: Package,
      label: t('feature_tab_stock') || 'Stock',
      title: t('feature_stock_title') || 'Stock Management',
      desc: t('feature_stock_long_desc') || 'Keep track of every item in your shop. Get alerts when stock is low, scan supplier receipts with AI to auto-update inventory, and set reorder points.',
      features: [
        t('feature_stk_f1') || 'Real-time stock tracking',
        t('feature_stk_f2') || 'AI-powered receipt scanning',
        t('feature_stk_f3') || 'Low stock alerts',
        t('feature_stk_f4') || 'Bulk inline editing (qty, cost, price)',
        t('feature_stk_f5') || 'Barcode scanner integration',
        t('feature_stk_f6') || 'CSV import/export',
      ],
      image: null,
    },
    {
      icon: Truck,
      label: t('feature_tab_delivery') || 'Delivery',
      title: t('feature_delivery_title') || 'Home Delivery',
      desc: t('feature_delivery_long_desc') || 'Manage delivery orders, assign drivers, and track order status from pending to delivered. Configure delivery zones and fees.',
      features: [
        t('feature_del_f1') || 'Driver management & assignment',
        t('feature_del_f2') || 'Order status tracking',
        t('feature_del_f3') || 'Configurable delivery zones',
        t('feature_del_f4') || 'Customer address management',
        t('feature_del_f5') || 'Delivery fee configuration',
        t('feature_del_f6') || 'Real-time order updates',
      ],
      image: null,
    },
    {
      icon: BarChart3,
      label: t('feature_tab_reports') || 'Reports',
      title: t('feature_reports_title') || 'End of Day Reports',
      desc: t('feature_reports_long_desc') || 'Close your day with confidence. See daily sales totals, top-selling products, hourly breakdown, and export everything to CSV. Custom date range reports available.',
      features: [
        t('feature_rep_f1') || 'Daily sales summaries',
        t('feature_rep_f2') || 'Hourly sales breakdown',
        t('feature_rep_f3') || 'Top selling products',
        t('feature_rep_f4') || 'Custom date range reports',
        t('feature_rep_f5') || 'Monthly and yearly tabs',
        t('feature_rep_f6') || 'Export to CSV',
      ],
      image: null,
    },
  ];

  const current = tabs[activeTab];

  return (
    <section
      id="features"
      className="py-24 md:py-32 bg-white"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] tracking-tight mb-4">
            {t('features_title') || 'Everything You Need'}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('features_subtitle') || 'From POS to invoices, stock management and much more'}
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-2 transition-all ${
                activeTab === i
                  ? 'bg-[#1e3a5f] border-[#1e3a5f] text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-[#1e3a5f]/30'
              }`}
              data-testid={`feature-tab-${i}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Left: Text */}
            <div className="space-y-6">
              <div className="w-14 h-14 bg-[#1e3a5f] flex items-center justify-center">
                <current.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] tracking-tight">
                {current.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {current.desc}
              </p>
              <ul className="space-y-3">
                {current.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#22c55e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#22c55e]" />
                    </div>
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Image/Visual */}
            <div className="relative">
              {current.image ? (
                <div className="border-2 border-slate-100 bg-slate-50 p-2 shadow-xl">
                  <img
                    src={current.image}
                    alt={current.title}
                    className="w-full h-auto"
                    data-testid="feature-image"
                  />
                </div>
              ) : (
                <div className="border-2 border-slate-100 bg-[#1e3a5f] p-8 shadow-xl min-h-[320px] flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <current.icon className="w-16 h-16 text-white/20 mx-auto" />
                    <div className="space-y-2">
                      {current.features.slice(0, 4).map((f, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2">
                          <Check className="w-4 h-4 text-[#22c55e]" />
                          <span className="text-white/70 text-sm">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default FeaturesSection;
